/**
 * Who wrote a message: the avatar, the name, and the card both of them open.
 *
 * ## One card, two triggers
 *
 * {@link ChannelAuthorHoverCard} is the only definition of that card anywhere. It used to
 * be written out three times — here, in the office order-detail chats, and in the member
 * order thread — which is how the three had already drifted apart on the avatar they drew
 * inside themselves and on the role they named. The avatar beside a message had no card at
 * all, so the person's full name was reachable from the name and from nothing else.
 *
 * The card takes its TRIGGER as children, so the same component serves a name, an avatar,
 * or anything else a surface wants to hang it off. `ConversationMessageItem` wraps the
 * message avatar with it, which is what gives every surface the card without each one
 * wiring it up: the avatar a surface renders through `renderAvatar` is the trigger.
 *
 * ## The avatar is presence-free, the card's is not
 *
 * This module's own `ChannelAuthorAvatar` draws no online dot: it is the fallback for
 * surfaces that pass no `renderAvatar`, and every surface that wants presence passes its
 * own. The card inside is a different question — it is drawn here, once, for every
 * surface, so it draws the presence avatar directly.
 */

import { cn } from "cn";
import { useUserDetail } from "@/api/use-user-detail";
import Avatar from "@/components/ui/avatar/avatar";
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from "@/components/ui/hover-card";
import { Skeleton } from "@/components/ui/skeleton";
import { UserOnlineAvatar } from "@/features/my/orders/detail/_components/user-online-avatar";
import { getUserRoleLabel } from "@/libs/user-role";

export function ChannelAuthorAvatar({
  userId,
  size = "md",
}: {
  userId?: string;
  /** `lg` is the search result row; everything else takes the default. */
  size?: "md" | "lg";
}) {
  const { data: user, isLoading } = useUserDetail(userId ?? "", {
    enabled: !!userId,
  });

  // The skeleton tracks `size`, because the two drifting apart is the original bug: the
  // default `md` is `size-8` — the size of the `UserOnlineAvatar` every surface
  // substitutes through `renderAvatar` — while Avatar's own default is `size-6`, which
  // made this one shrink by 8px the moment the user row resolved.
  if (!userId || isLoading) {
    return (
      <Skeleton
        className={cn("rounded-full", size === "lg" ? "size-10" : "size-8")}
      />
    );
  }

  return (
    <Avatar
      size={size}
      src={user?.avatarUrl ?? ""}
      name={user?.name ?? ""}
      className="shrink-0"
    />
  );
}

export function ChannelAuthorName({
  userId,
  className,
}: {
  userId?: string;
  /**
   * Styles the `<p>` itself, because `HoverCardTrigger` is `asChild` and that `<p>` is
   * the rendered element. A `truncate` on a wrapper would clip without an ellipsis — the
   * ellipsis is painted by the box whose own inline content overflows.
   */
  className?: string;
}) {
  const { data: user } = useUserDetail(userId ?? "", { enabled: !!userId });

  if (!user) return null;

  return (
    <ChannelAuthorHoverCard userId={userId}>
      <p className={className}>{user.firstName || user.name}</p>
    </ChannelAuthorHoverCard>
  );
}

/**
 * The author's full name and role, on hover over `children`.
 *
 * `children` is handed to `HoverCardTrigger asChild`, so it must be a single element that
 * takes a ref — Radix measures it to place the card. A plain element (`<p>`, a `<span>`
 * around an avatar) is always safe; a bare function component is not, because a component
 * that does not forward its ref leaves the card with nothing to anchor to and it opens in
 * the page's top-left corner.
 *
 * The role is the person's — Admin, Manager, Technician — and not their role on the home.
 * The home role answers a different question ("is this the owner or a tenant?") and the
 * conversation already prints it beside the name on every message, so the card would only
 * be repeating the row it hangs off.
 */
export function ChannelAuthorHoverCard({
  userId,
  children,
}: {
  userId?: string;
  children: React.ReactNode;
}) {
  const { data: user } = useUserDetail(userId ?? "", { enabled: !!userId });

  // Nothing to say about an author who has not resolved — the trigger renders bare rather
  // than opening an empty card.
  if (!user) return children;

  const roleLabel = getUserRoleLabel(user.defaultRole || user.roles?.[0] || "");

  return (
    <HoverCard>
      <HoverCardTrigger asChild>{children}</HoverCardTrigger>
      <HoverCardContent
        side="top"
        align="start"
        className="bg-bg-surface-elevate-low border-border-dark-low px-3 py-2 rounded-xl"
      >
        <div className="flex items-center gap-2">
          <UserOnlineAvatar userId={userId} size="lg" />
          <div className="flex flex-col">
            <p className="text-sm font-semibold">{user.name}</p>
            {/* `getUserRoleLabel` answers null for a user with no role at all, and the
                name should not sit above an empty line. */}
            {!!roleLabel && (
              <p className="text-text-dark-low text-xs">{roleLabel}</p>
            )}
          </div>
        </div>
      </HoverCardContent>
    </HoverCard>
  );
}
